import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { baseUrl } from '../../constants';

@Injectable({ providedIn: 'root' })
export class StaffService {
  private apiUrl = `${baseUrl}Staffs`;

  constructor(private http: HttpClient) {}

  private get headers() {
    const token = localStorage.getItem('token');
    return {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      })
    };
  }

  getAll(filters?: { search?: string; department?: string; isActive?: boolean | null }): Observable<any[]> {
    let params = new HttpParams();
    if (filters?.search) params = params.set('search', filters.search);
    if (filters?.department) params = params.set('department', filters.department);
    if (filters?.isActive !== null && filters?.isActive !== undefined) {
      params = params.set('isActive', String(filters.isActive));
    }

    return this.http.get<any[]>(this.apiUrl, { ...this.headers, params });
  }

  getById(id: number): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/${id}`, this.headers);
  }

  getMyProfile(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/my/profile`, this.headers);
  }

  create(payload: any): Observable<any> {
    return this.http.post(`${this.apiUrl}`, payload, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any);
  }

  update(id: number, payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/${id}`, payload, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any);
  }

  delete(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, {
      ...this.headers,
      observe: 'response',
      responseType: 'text'
    } as any);
  }

  updateMyProfile(payload: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/my/profile`, payload, this.headers);
  }

  getRequestTargets(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/request-targets`, this.headers);
  }

  getMyRequests(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}StaffRequests/my`, this.headers);
  }

  createRequest(payload: any): Observable<any> {
    return this.http.post(`${baseUrl}StaffRequests`, payload, this.headers);
  }

  getAllRequests(filters?: { status?: string; requestType?: string; staffId?: number | null; directedToStaffId?: number | null }): Observable<any[]> {
    let params = new HttpParams();
    if (filters?.status) params = params.set('status', filters.status);
    if (filters?.requestType) params = params.set('requestType', filters.requestType);
    if (filters?.staffId) params = params.set('staffId', String(filters.staffId));
    if (filters?.directedToStaffId) params = params.set('directedToStaffId', String(filters.directedToStaffId));

    return this.http.get<any[]>(`${baseUrl}StaffRequests`, { ...this.headers, params });
  }

  approveRequest(id: number, comment?: string): Observable<any> {
    return this.http.put(`${baseUrl}StaffRequests/${id}/approve`, { comment: comment || null }, this.headers);
  }

  rejectRequest(id: number, comment?: string): Observable<any> {
    return this.http.put(`${baseUrl}StaffRequests/${id}/reject`, { comment: comment || null }, this.headers);
  }

  getInboxMessages(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}StaffMessages/inbox`, this.headers);
  }

  getSentMessages(): Observable<any[]> {
    return this.http.get<any[]>(`${baseUrl}StaffMessages/sent`, this.headers);
  }

  sendMessage(payload: {
    subject: string;
    body: string;
    sendToAllStaff?: boolean;
    recipientStaffIds?: number[];
    attachmentName?: string | null;
    attachmentContentType?: string | null;
    attachmentDataUrl?: string | null;
  }): Observable<any> {
    return this.http.post(`${baseUrl}StaffMessages/send`, payload, this.headers);
  }

  markMessageRead(recipientId: number): Observable<any> {
    return this.http.put(`${baseUrl}StaffMessages/read/${recipientId}`, {}, this.headers);
  }

  markReplyAlertsRead(): Observable<any> {
    return this.http.put(`${baseUrl}StaffMessages/read-replies`, {}, this.headers);
  }

  replyToMessage(messageId: number, payload: string | {
    replyText: string;
    attachmentName?: string | null;
    attachmentContentType?: string | null;
    attachmentDataUrl?: string | null;
  }): Observable<any> {
    const body = typeof payload === 'string' ? { replyText: payload } : payload;
    return this.http.put(`${baseUrl}StaffMessages/reply/${messageId}`, body, this.headers);
  }

  updateMessageRecipients(messageId: number, recipientStaffIds: number[]): Observable<any> {
    return this.http.put(`${baseUrl}StaffMessages/${messageId}/recipients`, { recipientStaffIds }, this.headers);
  }

  closeThread(messageId: number): Observable<any> {
    return this.http.put(`${baseUrl}StaffMessages/close/${messageId}`, {}, this.headers);
  }

  getUnreadMessageCount(): Observable<{ unread: number }> {
    return this.http.get<{ unread: number }>(`${baseUrl}StaffMessages/unread-count`, this.headers);
  }
}
